// Mounts @dehub/help for the order app: router + redux wiring, static seed until the help API exists (spec 13 §B1).
import { HelpProvider, type HelpContext, type HelpSeedRow } from "@dehub/theme"
import React, { useCallback, useMemo } from "react"
import { useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { IAuth } from "../store/Auth"
import seedMn from "./seed/order.mn.json"
import { HELP_SCREEN_TITLES, resolveHelpScreen } from "./screen-map"

const SEED: Record<string, HelpSeedRow[]> = { mn: seedMn as HelpSeedRow[] }

export function HelpRoot({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const navigate = useNavigate()
  const user = useSelector((s: { auth: IAuth }) => s.auth.user)
  const lang = "mn"   // bundle language; en/ko/zh fall back to mn (spec 10 §4.5) until translated seeds exist

  const role = user?.currentBusiness?.type === "SUPPLIER" ? "SUPPLIER" : "BUYER"
  const permCodes = useMemo(() => (user?.permissions || []).map((p: { code: string }) => p.code), [user?.permissions])
  const getContext = useCallback((): Partial<HelpContext> => ({ role, perms: permCodes }), [role, permCodes])

  const onAction = useCallback((name: string, payload?: unknown) => {
    // Pages subscribe through a DOM event so the provider stays decoupled from page state.
    window.dispatchEvent(new CustomEvent("dehub-help-action", { detail: { name, payload } }))
  }, [])

  if (!user?.id) return <>{children}</>
  return (
    <HelpProvider
      app="order"
      lang={lang}
      pathname={location.pathname}
      resolveScreen={resolveHelpScreen}
      getContext={getContext}
      fetchBundle={null}
      staticBundle={SEED[lang]}
      preferStatic
      navigate={(route) => navigate(route)}
      onAction={onAction}
      screenTitles={HELP_SCREEN_TITLES}
      // Same 3-tab drawer as network (user 2026-11-03): fields stay reachable from the «?» icons.
      drawerTabs={["page", "status", "faq"]}
    >
      {children}
    </HelpProvider>
  )
}

/** Subscribe to a help panel action (e.g. «Зөвхөн эдгээрийг харуулах» → "filter-pending"). */
export function useHelpAction(name: string, handler: () => void) {
  React.useEffect(() => {
    const h = (e: Event) => { if ((e as CustomEvent).detail?.name === name) handler() }
    window.addEventListener("dehub-help-action", h)
    return () => window.removeEventListener("dehub-help-action", h)
  }, [name, handler])
}
